import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/authContext'
import { useApiData } from '../../contexts/apiService';
import { producersMarkerColor as producersMarkerColorGenerated } from './utils/generateColor';
import { getLatLngCenter } from './utils/coordinateCalc';
import MapComponent from './components/mapMainComponent.jsx';
import LeaseDropdown from './components/LeaseDropdown.jsx'
import StationDropdown from './components/StationDropdown.jsx'
import AddCoordinateSection from './components/AddCoordinateSection.jsx'
import MapControlButtons from './components/MapControlButtons.jsx'
import ProducersSection from './components/ProducersSection.jsx'
import MarketsSection from './components/MarketsSection.jsx'
import DataInput from './profit_analysis/main/DataInput'
import RFPTab from './rfp/RFPTab'
import TabToggle from '../../components/common/tab_toggle/tabToggle'
import { useDataManager } from '../../contexts/dataManager';
import { useUserAccess, USER_ROLE_LEVEL } from '../../contexts/userAccess';

import { useJsApiLoader } from '@react-google-maps/api';

import "./style.css"
import { ErrorBoundary } from 'react-error-boundary';

const GOOGLE_MAPS_LIBRARIES = ['places'];

const INITIAL_CAMERA = {
    center: { lat: 39.8097343, lng: -98.5556199 },
    zoom: 5
};

const Home = () => {
    const { currentUser } = useAuth()
    const { apiData, isLoading } = useApiData()

    const [randomCoordenateValue, setRandomCoordenateValue] = useState(null);
    const [randomCoordinateValueError, setRandomCoordinateValueError] = useState(false);
    
    const [secondRandomCoordenateValue, setSecondRandomCoordenateValue] = useState(null);
    const [secondRandomCoordinateValueError, setSecondRandomCoordinateValueError] = useState(false);

    const [mapMarkerData, setMapMarkerData] = useState([])
    const [manualInputData, setManualInputData] = useState([])

    const [randomCoordenateMapMarkerData, setRandomCoordenateMapMarkerData] = useState([])
    const [secondRandomCoordenateMapMarkerData, setSecondRandomCoordenateMapMarkerData] = useState([])

    const [leaseCoordenates, setLeaseCoordenates] = useState([])
    const [stationCoordenates, setStationCoordenates] = useState([])

    const [closeInfo, setCloseInfo] = useState(null)
    const [cameraProps, setCameraProps] = useState(INITIAL_CAMERA);
    const [isLoadingZoomCamera, setIsLoadingZoomCamera] = useState(null)
    const [selectedCoordenate, setSelectedCoordenate] = useState(null)
    const [selectedStationCoordenate, setSelectedStationCoordenate] = useState(null)

    const [routeCalculation, setRouteCalculation] = useState(null);
    const [routeCalculations, setRouteCalculations] = useState(null);
    const [selectSearchValue, setSelectSearchValue] = useState();
    const [selectStationSearchValue, setSelectStationSearchValue] = useState();

    const [showedMarkerData, setShowedMarkerData] = useState([])
    const [formattedLeaseData, setFormattedLeaseData] = useState([])
    const [formattedStationData, setFormattedStationData] = useState([])

    const [producerCheckboxValues, setProducerCheckboxValues] = useState([])
    const [marketCheckboxValues, setMarketCheckboxValues] = useState([])
    const [selectAllProducerCheckboxValue, setSelectAllProducerCheckboxValue] = useState(false)
    const [selectAllMarketsCheckboxValue, setSelectAllMarketsCheckboxValue] = useState(false)

    const [isClustered, setIsClustered] = useState(false)
    const [openWindowSelectedCoordenate, setOpenWindowSelectedCoordenate] = useState(null)
    const [openWindowSelectedMarketCoordenate, setOpenWindowSelectedMarketCoordenate] = useState(null)

    const [leaseSelectedColor, setLeaseSelectedColor] = useState("")
    const [stationSelectedColor, setStationSelectedColor] = useState("")

    const [showAddCoordinateDropdown, setShowAddCoordinateDropdown] = useState(false)

    const [truckingRates, setTruckingRates] = useState([])

    const [toggleInputView, setToggleInputView] = useState(false)
    const [mapTab, setMapTab] = useState('map') // 'map' | 'rfp'
    const { userRole = 'viewer' } = useUserAccess()
    const SCHEDULE_ADMIN_LEVEL = USER_ROLE_LEVEL['schedule_admin'];
    const canAccessRFP = Number(USER_ROLE_LEVEL[userRole]) >= SCHEDULE_ADMIN_LEVEL;
    const { requestTableData } = useDataManager()

    useEffect(() => {
        requestTableData('lease_coordinates')
        requestTableData('market_coordinates')
        requestTableData('trucking_rates')
        requestTableData('components_pricing')
        requestTableData('market_discounts')
    }, [])


    const handleCameraChange = useCallback((ev) => {
        setCameraProps(ev.detail)
    });


    const handleAddToMap = (inputData) => {
        // Add manual input data to existing map data
        const newMapData = [...showedMarkerData, ...inputData];
        setShowedMarkerData(newMapData);
        
        // Store manual input data separately if needed
        setManualInputData(prevData => [...prevData, ...inputData]);
        
        // Optional: Focus map on the new points
        if (inputData.length > 0) {
            const firstPoint = inputData[0];
            const mapLocation = {
                center: {
                    lat: firstPoint.lat,
                    lng: firstPoint.lng,
                },
                zoom: inputData.length === 1 ? 12 : 8 // Zoom based on number of points
            };
            setCameraProps(mapLocation);
        }
    };

    useJsApiLoader({
        googleMapsApiKey: 'AIzaSyArQc3VJ5UufQwcoWIRUQIbdrVQ-UlhaRc',
        libraries: GOOGLE_MAPS_LIBRARIES,
    });
    
    const calculateDistance = (origin, destination) => {
        const directionsService = new window.google.maps.DirectionsService();

        return directionsService
        .route({
            origin,
            destination,
            travelMode: 'DRIVING',
        })
    };

    useEffect(() => {
        if (apiData && apiData.lease_coordinates && apiData.market_coordinates && apiData.market_discounts && apiData.components_pricing) {
            let producersMarkerColor = [...producersMarkerColorGenerated]
            const colorByProducer = {};

            // Preserve the user's existing lease/station selections across socket-driven
            // apiData updates. Without this, every time the underlying data actually changes
            // (a lease/market gets added, edited, or removed), the checkbox lists were rebuilt
            // from scratch with checked: false, wiping out anything the user had selected.
            const previousLeaseCheckedByKey = new Map()
            producerCheckboxValues.forEach(producerItem => {
                producerItem.value.forEach(leaseItem => {
                    previousLeaseCheckedByKey.set(leaseItem.key, leaseItem.checked)
                })
            });

            const previousStationCheckedByKey = new Map()
            marketCheckboxValues.forEach(marketItem => {
                marketItem.value.forEach(stationItem => {
                    previousStationCheckedByKey.set(stationItem.key, stationItem.checked)
                })
            });

            (apiData.lease_coordinates || []).sort((a, b) => {
                const aProducer = a.producer_name || a.producer || '';
                const bProducer = b.producer_name || b.producer || '';
                if (aProducer < bProducer) {
                    return -1;
                }
                if (aProducer > bProducer) {
                    return 1;
                }
                return 0;
            });

            const truckingRatesByCarrier = apiData?.trucking_rates?.reduce((acc, truckingRate) => {
                const carrierKey = Object.keys(truckingRate).find(key => key !== 'id' && key !== 'mileage')
                if(!acc[carrierKey]) {
                    acc[carrierKey] = []
                }

                const rateData = {
                    id: truckingRate.id,
                    mileage: truckingRate.mileage,
                    rate: truckingRate[carrierKey]
                }
                acc[carrierKey].push(rateData)

                return acc
            }, {})

            setTruckingRates(truckingRatesByCarrier)

            const { formattedData, dataByProducer } = apiData?.lease_coordinates.reduce((acc, { lease, latitude, longitude, producer, producer_name, api, bpd, bsw }, index) => {
                let lat = Number(latitude)
                const lng = Number(longitude)
                const producerLabel = producer_name || producer
                if (latitude && longitude && lease) {
                    if (!colorByProducer[producerLabel]) {
                        if (producersMarkerColor.length === 0) {
                            producersMarkerColor = [...producersMarkerColorGenerated]
                        }
                        colorByProducer[producerLabel] = producersMarkerColor.pop()
                    }
                    const data = {
                        lease,
                        lat,
                        lng,
                        producer: producerLabel,
                        api: 40,
                        bsw: 1.63,
                        bpd,
                        color: colorByProducer[producerLabel],
                        key: JSON.stringify({ name: lease, lat, lng })
                    }
                    acc.formattedData.push(data)

                    if (producerLabel && !acc.dataByProducer[producerLabel]) {
                        acc.dataByProducer[producerLabel] = []
                    }
                    if (producerLabel) acc.dataByProducer[producerLabel].push(data)
                }

                return acc
            }, { formattedData: [], dataByProducer: {} });

            setFormattedLeaseData(formattedData)

            apiData?.market_coordinates.sort((a, b) => {
                if (a.market < b.market) {
                    return -1;
                }
                if (a.market > b.market) {
                    return 1;
                }
                return 0;
            });

            const { dataByMarket, formattedStationData } = apiData?.market_coordinates.reduce((acc, { latitude, longitude, market, station }, index) => {
                let lat = Number(latitude)
                const lng = Number(longitude)
                if (latitude && longitude && market) {
                    const data = {
                        lat,
                        lng,
                        market,
                        station,
                        isMarket: true,
                        color: "red",
                        key: JSON.stringify({ name: market, lat, lng })
                    }

                    acc.formattedStationData.push(data)

                    if (!acc.dataByMarket[market]) {
                        acc.dataByMarket[market] = []
                    }
                    acc.dataByMarket[market].push(data)
                }

                return acc
            }, { dataByMarket: {}, formattedStationData: [] });

            setFormattedStationData(formattedStationData)
            setMapMarkerData([])

            const coordenates = formattedData.map(item => (
                {
                    value: { lat: item.lat, lng: item.lng },
                    label: `${item.lease} - ${item.producer}`
                }
            ))

            setLeaseCoordenates(coordenates)

            const producerData = Object.keys(dataByProducer).map(producerName => (
                {
                    value: dataByProducer[producerName].map(producerLeaseData => (
                        {
                            ...producerLeaseData,
                            checked: previousLeaseCheckedByKey.get(producerLeaseData.key) ?? false
                        }
                    )),
                    label: `${producerName} (${dataByProducer[producerName].length})`,
                    checked: false,
                    color: colorByProducer[producerName],
                    isOpenDropdown: false,
                }
            ))

            producerData.forEach(productItem => (
                productItem.value.sort((a, b) => {
                    if (a.lease < b.lease) {
                        return -1;
                    }
                    if (a.lease > b.lease) {
                        return 1;
                    }
                    return 0;
                })
            ))

            // Recompute the group-level checked flag now that individual leases may carry
            // over a previously checked state.
            producerData.forEach(productItem => {
                productItem.checked = productItem.value.length > 0 && productItem.value.every(v => v.checked)
            })

            setProducerCheckboxValues(producerData)
            setSelectAllProducerCheckboxValue(producerData.length > 0 && producerData.every(p => p.checked))

            const stationCoordenates = formattedStationData.map(item => (
                {
                    value: { lat: item.lat, lng: item.lng },
                    label: `${item.station} - ${item.market}`
                }
            ))
            setStationCoordenates(stationCoordenates)

            const getFormulaPrice = (marketDiscountData) => {
                const allComponents = apiData.components_pricing
                const marketComponents = []
                allComponents.forEach(componentData => {
                    const componentName = componentData.component
                    if(marketDiscountData[componentName]) {
                        marketComponents.push(componentData)
                    }
                })
                const componentsPrice = marketComponents.reduce((acc, component) => (
                    acc + Number(component.price)
                ), 0)

                const price = marketDiscountData.discount ? componentsPrice - Number(marketDiscountData.discount)
                : componentsPrice - (componentsPrice * Number(marketDiscountData.disc_percentage) / 100)

                const marketComponentsName = marketComponents.map(({ component: name }) => name)
                const formula = `${marketComponentsName.join(' + ')} - ${marketDiscountData.discount ? 'discount' : 'discount percentage'}`

                return {
                    price: parseFloat(price.toFixed(4)),
                    formula,
                    discount: marketDiscountData.discount,
                    discountPercentage: marketDiscountData.disc_percentage
                }
            }

            const getStationPrice = (stationData) => {
                const marketDiscountData = apiData?.market_discounts.filter(marketPrice => (
                    marketPrice.market === stationData.market && marketPrice.station === stationData.station 
                ))

                const stationPriceResult = {}
                marketDiscountData.forEach(marketPrice => {
                    const { price, formula, discount, discountPercentage } = getFormulaPrice(marketPrice)
                    if(marketPrice.gravity) {
                        if(!stationPriceResult.gravity) {
                            stationPriceResult.gravity = []
                        }
                        const gravityPrice = {
                            low: marketPrice.gravity_low,
                            high: marketPrice.gravity_high,
                            value: price,
                            formula: formula,
                            discount: discount,
                            discountPercentage: discountPercentage,
                        }
                        stationPriceResult.gravity.push(gravityPrice)
                    } else if(marketPrice.bsw) {
                        if(!stationPriceResult.bsw) {
                            stationPriceResult.bsw = []
                        }
                        const bswPrice = {
                            low: marketPrice.bsw_low,
                            high: marketPrice.bsw_high,
                            value: price,
                            formula: formula,
                            discount: discount,
                            discountPercentage: discountPercentage,
                        } 
                        stationPriceResult.bsw.push(bswPrice)
                    } else {
                        stationPriceResult.value = price
                        stationPriceResult.formula = formula
                        stationPriceResult.discount = discount
                        stationPriceResult.discountPercentage = discountPercentage
                    }
                })

                return stationPriceResult
            }

            const marketData = Object.keys(dataByMarket).map(marketName => {
                return {
                    value: dataByMarket[marketName].map(singleMarketData => (
                        {
                            ...singleMarketData,
                            checked: previousStationCheckedByKey.get(singleMarketData.key) ?? false,
                            price: getStationPrice(singleMarketData),
                        }
                    )),
                    label: `${marketName} (${dataByMarket[marketName].length})`,
                    checked: false,
                    isOpenDropdown: false,
                }
            })

            marketData.forEach(marketItem => (
                marketItem.value.sort((a, b) => {
                    if (a.station < b.station) {
                        return -1;
                    }
                    if (a.station > b.station) {
                        return 1;
                    }
                    return 0;
                })
            ))

            // Recompute the group-level checked flag now that individual stations may carry
            // over a previously checked state.
            marketData.forEach(marketItem => {
                marketItem.checked = marketItem.value.length > 0 && marketItem.value.every(v => v.checked)
            })

            setMarketCheckboxValues(marketData)
            setSelectAllMarketsCheckboxValue(marketData.length > 0 && marketData.every(m => m.checked))

            // Keep the currently displayed markers in sync: drop any lease/station marker that
            // no longer exists in the latest data (e.g. it was deleted in the broadcast), and
            // refresh the ones that still exist so stale coordinates/labels don't linger.
            // Manually added points (from the DataInput tab) use a distinct "manual-" key prefix
            // and are left untouched.
            const currentLeaseByKey = new Map(formattedData.map(item => [item.key, item]))
            const currentStationByKey = new Map(formattedStationData.map(item => [item.key, item]))

            setShowedMarkerData(prevShowedMarkerData => (
                prevShowedMarkerData.reduce((acc, marker) => {
                    if (typeof marker.key === 'string' && marker.key.startsWith('manual-')) {
                        acc.push(marker)
                        return acc
                    }
                    const refreshedLease = currentLeaseByKey.get(marker.key)
                    if (refreshedLease) {
                        acc.push(refreshedLease)
                        return acc
                    }
                    const refreshedStation = currentStationByKey.get(marker.key)
                    if (refreshedStation) {
                        acc.push(refreshedStation)
                        return acc
                    }
                    // Marker no longer exists in the latest lease/station data - drop it.
                    return acc
                }, [])
            ))
        }
    
    }, [apiData?.lease_coordinates, apiData?.market_coordinates, apiData?.market_discounts, apiData?.components_pricing])

    const handleSelectCoordenate = (event) => {
        setSelectSearchValue(event)
        const mapLocation = {
            center: event.value,
            zoom: 12
        };

        let selectedLease = mapMarkerData.find(lease => (
            (lease.lat === event.value.lat) && (lease.lng === event.value.lng)
        ))

        if (selectedLease) {
            setSelectedCoordenate(event.value)
            setOpenWindowSelectedCoordenate(event.value)
        } else {
            selectedLease = formattedLeaseData.find(lease => (
                (lease.lat === event.value.lat) && (lease.lng === event.value.lng)
            ))

            const newMapMarkerData = [...mapMarkerData];
            newMapMarkerData.push(selectedLease)
            setSelectedCoordenate(selectedLease)
            setShowedMarkerData(newMapMarkerData)

            setOpenWindowSelectedCoordenate(selectedLease)
        }

        const { key: currentSelectedLeaseKey } = selectedLease

        let previousSelectedLeaseKey;
        if (selectedCoordenate) {
            previousSelectedLeaseKey = selectedCoordenate.key
        }

        let producerIndex
        let leaseIndex
        let previousProducerIndex
        let previousLeaseIndex
        for (let producerIndexIt = 0; producerIndexIt < producerCheckboxValues.length; producerIndexIt++) {
            const producerData = producerCheckboxValues[producerIndexIt];

            for (let leaseIndexIt = 0; leaseIndexIt < producerData.value.length; leaseIndexIt++) {
                const leaseData = producerData.value[leaseIndexIt];
                if (leaseData.key === currentSelectedLeaseKey) {
                    producerIndex = producerIndexIt;
                    leaseIndex = leaseIndexIt;
                } else if (previousSelectedLeaseKey && leaseData.key === previousSelectedLeaseKey) {
                    previousProducerIndex = producerIndexIt;
                    previousLeaseIndex = leaseIndexIt;
                }

                if (previousSelectedLeaseKey && previousProducerIndex && producerIndex) {
                    break
                }

                if (!previousSelectedLeaseKey && producerIndex) {
                    break
                }
            }

            if (previousSelectedLeaseKey && previousProducerIndex && producerIndex) {
                break
            }

            if (!previousSelectedLeaseKey && producerIndex) {
                break
            }
        }

        const newProducerCheckboxValues = [...producerCheckboxValues]
        newProducerCheckboxValues[producerIndex].value[leaseIndex].checked = true

        const allLeasesChecked = newProducerCheckboxValues[producerIndex].value.every(lease => (
            lease.checked === true
        ))
        if (allLeasesChecked) {
            newProducerCheckboxValues[producerIndex].checked = true
        } else {
            newProducerCheckboxValues[producerIndex].checked = false
        }

        if (!(previousProducerIndex === undefined || previousProducerIndex === null)) {
            newProducerCheckboxValues[previousProducerIndex].checked = false
            newProducerCheckboxValues[previousProducerIndex].value[previousLeaseIndex].checked = false
        }

        setProducerCheckboxValues(newProducerCheckboxValues)

        setCameraProps(mapLocation)

        const latitude = event.value.lat
        const longitude = event.value.lng
        const coordinate = `${latitude}, ${longitude}`
        setRandomCoordenateValue(coordinate)

        setLeaseSelectedColor(newProducerCheckboxValues[producerIndex].value[leaseIndex].color)
    }

    const handleSelectStationCoordenate = (event) => {
        setSelectStationSearchValue(event)
        const mapLocation = {
            center: event.value,
            zoom: 12
        };

        let selectedStation = mapMarkerData.find(station => (
            (station.lat === event.value.lat) && (station.lng === event.value.lng)
        ))

        if (selectedStation) {
            setSelectedStationCoordenate(event.value)

            setOpenWindowSelectedMarketCoordenate(event.value)
        } else {
            selectedStation = formattedStationData.find(station => (
                (station.lat === event.value.lat) && (station.lng === event.value.lng)
            ))

            setSelectedStationCoordenate(selectedStation)
            const currentMapMarkerData = [...mapMarkerData]

            let newMapMarkerData = marketCheckboxValues.filter(item => item.value.find(itemValue => itemValue.station === selectedStation.station && itemValue.market === selectedStation.market))
            newMapMarkerData = newMapMarkerData[0].value.find((item) => item.station === selectedStation.station && item.market === selectedStation.market)
            currentMapMarkerData.push(newMapMarkerData)
            setShowedMarkerData(currentMapMarkerData)

            setOpenWindowSelectedMarketCoordenate(selectedStation)
        }

        const { key: currentSelectedStationKey } = selectedStation

        let previousSelectedStationKey;
        if (selectedStationCoordenate) {
            previousSelectedStationKey = selectedStationCoordenate.key
        }

        let marketIndex
        let stationIndex
        let previousMarketIndex
        let previousStationIndex
        for (let marketIndexIt = 0; marketIndexIt < marketCheckboxValues.length; marketIndexIt++) {
            const marketData = marketCheckboxValues[marketIndexIt];

            for (let stationIndexIt = 0; stationIndexIt < marketData.value.length; stationIndexIt++) {
                const stationData = marketData.value[stationIndexIt];
                if (stationData.key === currentSelectedStationKey) {
                    marketIndex = marketIndexIt;
                    stationIndex = stationIndexIt;
                } else if (previousSelectedStationKey && stationData.key === previousSelectedStationKey) {
                    previousMarketIndex = marketIndexIt;
                    previousStationIndex = stationIndexIt;
                }

                if (previousSelectedStationKey && previousMarketIndex && marketIndex) {
                    break
                }

                if (!previousSelectedStationKey && marketIndex) {
                    break
                }
            }

            if (previousSelectedStationKey && previousMarketIndex && marketIndex) {
                break
            }

            if (!previousSelectedStationKey && marketIndex) {
                break
            }
        }

        const newMarketCheckboxValues = [...marketCheckboxValues]
        newMarketCheckboxValues[marketIndex].value[stationIndex].checked = true

        const allStationChecked = newMarketCheckboxValues[marketIndex].value.every(station => (
            station.checked === true
        ))
        if (allStationChecked) {
            newMarketCheckboxValues[marketIndex].checked = true
        } else {
            newMarketCheckboxValues[marketIndex].checked = false
        }

        if (!(previousMarketIndex === undefined || previousMarketIndex === null)) {
            newMarketCheckboxValues[previousMarketIndex].checked = false
            newMarketCheckboxValues[previousMarketIndex].value[previousStationIndex].checked = false
        }

        setMarketCheckboxValues(newMarketCheckboxValues)

        const latitude = event.value.lat
        const longitude = event.value.lng
        const coordinate = `${latitude}, ${longitude}`
        setSecondRandomCoordenateValue(coordinate)

        setCameraProps(mapLocation)
        setStationSelectedColor("red")
    }

    function calculateMidpoint(x1, y1, x2, y2) {
        const midpointX = (x1 + x2) / 2;
        const midpointY = (y1 + y2) / 2;
        return { lat: midpointX, lng: midpointY };
    }

    const calculateBestRoute = async () => {
        setRouteCalculation(null)
        if (!randomCoordenateValue || !secondRandomCoordenateValue) {
            return
        }
        let [latitude, longitude] = randomCoordenateValue.split(',')

        await Promise.resolve(setTimeout(() => { }, 3000))


        latitude = Number(latitude.trim())
        longitude = Number(longitude.trim())

        let [secondLatitude, secondLongitude] = secondRandomCoordenateValue.split(',')
        secondLatitude = Number(secondLatitude.trim())
        secondLongitude = Number(secondLongitude.trim())

        setRouteCalculation({
            origin: { lat: latitude, lng: longitude },
            destination: { lat: secondLatitude, lng: secondLongitude },
        })

        let selectedLeaseData
        for (let producerIndexIt = 0; producerIndexIt < producerCheckboxValues.length; producerIndexIt++) {
            const producerData = producerCheckboxValues[producerIndexIt];
            for (let leaseIndexIt = 0; leaseIndexIt < producerData.value.length; leaseIndexIt++) {
                const leaseData = producerData.value[leaseIndexIt];

                if (leaseData.lat == latitude && leaseData.lng === longitude) {
                    selectedLeaseData = leaseData
                    break
                }
            }

            if (selectedLeaseData) {
                break
            }
        }

        let selectedMarketData
        for (let marketIndexIt = 0; marketIndexIt < marketCheckboxValues.length; marketIndexIt++) {
            const marketData = marketCheckboxValues[marketIndexIt];

            for (let stationIndexIt = 0; stationIndexIt < marketData.value.length; stationIndexIt++) {
                const stationData = marketData.value[stationIndexIt];

                if (stationData.lat === secondLatitude && stationData.lng === secondLongitude) {
                    selectedMarketData = stationData
                    break
                }
            }

            if (selectedMarketData) {
                break
            }
        }

        if(!selectedLeaseData) {
            addCoordinate("first", false)
        } else {
            setOpenWindowSelectedCoordenate(selectedLeaseData)

        }

        if(!selectedMarketData) {
            addCoordinate("second", false)
        } else {
            setOpenWindowSelectedMarketCoordenate(selectedMarketData)
        }

        const midPoint = calculateMidpoint(latitude, longitude, secondLatitude, secondLongitude)
        setIsLoadingZoomCamera(true)
        const mapLocation = {
            center: {
                lat: midPoint.lat,
                lng: midPoint.lng,
            },
            zoom: 8
        };

        setCameraProps(mapLocation)

        await Promise.resolve(setTimeout(() => {                    
            setIsLoadingZoomCamera(false)
        }, 1500))
    }
    
    function degToRad(deg) {
        return deg * (Math.PI / 180);
    }

    function calculateCoordinateDistance(lat1, lon1, lat2, lon2) {
        const R = 6371; 
        const dLat = degToRad(lat2 - lat1);  
        const dLon = degToRad(lon2 - lon1);  
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.cos(degToRad(lat1)) * Math.cos(degToRad(lat2)) *
                  Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c; 
        return distance;
    }
    
    function degToRad(deg) {
        return deg * (Math.PI / 180);
    }
    
    function getClosestStraightLineMarkets(initialCoordinate, coordinates) {
        let coordinatesDistance = coordinates.map(coordinate => {
            const distance = calculateCoordinateDistance(
                initialCoordinate.lat, 
                initialCoordinate.lon, 
                coordinate.lat, 
                coordinate.lon
            );

            return { ...coordinate, distance };
        });

        coordinatesDistance.sort((a, b) => a.distance - b.distance);
        const closestMarketDistance = coordinatesDistance[0].distance;
        coordinatesDistance = coordinatesDistance.filter(item => Number(item.distance) - Number(closestMarketDistance) <= 150);
    
        return coordinatesDistance.slice(0, 5);
    }

    async function getClosestMarkets(initialCoordinate, marketsCoordinate) {
        const coordinatesDistancePromises = []
        for (let index = 0; index < marketsCoordinate.length; index++) {
            const marketCoordinate = marketsCoordinate[index];
            coordinatesDistancePromises.push(calculateDistance(
                {lat: initialCoordinate.lat, lng: initialCoordinate.lon},
                {lat: marketCoordinate.lat, lng: marketCoordinate.lon},
            ))
        }

        try {
        const distances = await Promise.all(coordinatesDistancePromises)

        const coordinatesDistance = distances.map((distanceResult) => {
            const [distance, ...rest] = distanceResult.routes[0].legs[0].distance.text.replace(",", "").split(" ");
            const duration = distanceResult.routes[0].legs[0].duration.text;

            const coordinateData = marketsCoordinate.find(market_coordinates => (
                market_coordinates.lat === distanceResult.request.destination.location.lat() &&
                market_coordinates.lon === distanceResult.request.destination.location.lng()
            ))

         
            return { ...coordinateData, duration, distance: Number(distance), station: coordinateData.station, name: coordinateData.market }
        })        

        coordinatesDistance.sort((a, b) => a.distance - b.distance);
        return coordinatesDistance.slice(0, 5);
        } catch(e) {
            console.log("error", e)
        }
    }

    const analyzeBestRoutes = async () => {
        let [leaseLat, leaseLon] = randomCoordenateValue.split(',')
        leaseLat = Number(leaseLat.trim())
        leaseLon = Number(leaseLon.trim())
        const selectedLeaseCoordinate = {
            lat: leaseLat,
            lon: leaseLon,
        }

        let selectedLease = mapMarkerData.find(lease => (
            (lease.lat === leaseLat) && (lease.lng === leaseLon)
        ))

        const stationCoordinates = []
        marketCheckboxValues.forEach(market => (
            market.value.forEach(station => {
                stationCoordinates.push({lat: station.lat, lon: station.lng, station: station.station, market: station.market, price: station.price })
            })
        ))

        const filterCloseMarkets = getClosestStraightLineMarkets(selectedLeaseCoordinate, stationCoordinates)
        const closestMarkets = await getClosestMarkets(selectedLeaseCoordinate, filterCloseMarkets)

        
        let newRouteDirections = []
        for (let index = 0; index < closestMarkets.length; index++) {
            const market = closestMarkets[index];

            newRouteDirections.push({
                origin: { lat: leaseLat, lng: leaseLon },
                destination: { lat: market.lat, lng: market.lon },
                distance: parseFloat(market.distance.toFixed(2)),
                station: market.station,
                duration: market.duration,
                market: market.name,
                price: market.price,
                leaseData: selectedLease,
            })
        }

        const pathColors = ['red', 'orange', 'yellow', 'dodgerblue', 'green']

        newRouteDirections = newRouteDirections.map(newDirection => ({
            ...newDirection,
            pathColor: pathColors.pop()
        }))

        const currentMarketMapMarkerData = marketCheckboxValues.reduce((acc, marketData) => {
            const currentSingleMarketData = marketData.value
            const singleMarketDataIncluded = []
            currentSingleMarketData.forEach(dataItem => {
                const isMarketMatch = closestMarkets.some(market => market.lat === dataItem.lat && market.lon === dataItem.lng)
                if (isMarketMatch) {
                    const { pathColor } = newRouteDirections.find(route => route.station === dataItem.station)
                    dataItem.color = pathColor
                    singleMarketDataIncluded.push(dataItem)
                }
            })

            return [...acc, ...singleMarketDataIncluded]
        }, [])

        setShowedMarkerData([selectedLease, ...currentMarketMapMarkerData])

        setRouteCalculations(newRouteDirections)

        const newMarketCheckboxValues = marketCheckboxValues.reduce((acc, marketData) => {
            const marketDataValue = marketData.value.map(item => {
                const isMarketMatch = closestMarkets.some(market => market.lat === item.lat && market.lon === item.lng)
                return {
                    ...item, checked: isMarketMatch ? true : false
                }
            })


            return [...acc, { ...marketData, value: marketDataValue, checked: false }]
        }, [])

        setMarketCheckboxValues(newMarketCheckboxValues)

        setIsLoadingZoomCamera(true)
        const mapLocation = {
            center: {
                lat: leaseLat,
                lng: leaseLon,
            },
            zoom: 9
        };

        setCameraProps(mapLocation)

        await Promise.resolve(setTimeout(() => {                    
            setIsLoadingZoomCamera(false)
        }, 1500))
    }

    const clearMap = () => {
        setCameraProps(INITIAL_CAMERA)
        setRouteCalculation(null)
        setRouteCalculations(null)
        setSelectedCoordenate(null)
        setSelectedStationCoordenate(null)
        setSelectStationSearchValue(null)
        setSelectSearchValue(null)
        setSelectAllProducerCheckboxValue(false)
        setSelectAllMarketsCheckboxValue(false)
        setRandomCoordenateMapMarkerData([])
        setSecondRandomCoordenateMapMarkerData([])
        setRandomCoordenateValue("")
        setSecondRandomCoordenateValue("")
        setShowedMarkerData([])
        setCloseInfo(false)
        setLeaseSelectedColor("")
        setStationSelectedColor("")


        const newProducerCheckboxValues = producerCheckboxValues.reduce((acc, producerData) => {
            const producerDataValue = producerData.value.map(item => ({
                ...item, checked: false
            }))

            return [...acc, { ...producerData, value: producerDataValue, checked: false }]
        }, [])

        setProducerCheckboxValues(newProducerCheckboxValues)

        const newMarketCheckboxValues = marketCheckboxValues.reduce((acc, marketData) => {
            const marketDataValue = marketData.value.map(item => ({
                ...item, checked: false
            }))


            return [...acc, { ...marketData, value: marketDataValue, checked: false }]
        }, [])

        setMarketCheckboxValues(newMarketCheckboxValues)
    }

    const resetCalculation = () => {
        setRouteCalculation(null)
        setRouteCalculations(null)
        setRandomCoordenateMapMarkerData([])
        setSecondRandomCoordenateMapMarkerData([])
    }

    useEffect(() => {
        setMapMarkerData(showedMarkerData)
    }, [showedMarkerData])

    const toggleDropdown = (index) => {
        const newProducerCheckboxValues = [...producerCheckboxValues]
        newProducerCheckboxValues[index] = { ...newProducerCheckboxValues[index], isOpenDropdown: !(newProducerCheckboxValues[index].isOpenDropdown) }
        setProducerCheckboxValues(newProducerCheckboxValues)
    }

    const toggleMarketDropdown = (index) => {
        const newMarketCheckboxValues = [...marketCheckboxValues]
        newMarketCheckboxValues[index] = { ...newMarketCheckboxValues[index], isOpenDropdown: !(newMarketCheckboxValues[index].isOpenDropdown) }
        setMarketCheckboxValues(newMarketCheckboxValues)
    }

    const resetMapInputData = () => {
        setSelectSearchValue(null)
        setSelectedCoordenate(null)
        setShowedMarkerData([])
        setSelectAllProducerCheckboxValue(false)
        setCloseInfo(true)
    }

    const handleCheck = (checked, index, entity) => {
        resetMapInputData()
        
        let primaryEntityCheckboxValues
        let primarySetEntityCheckboxValues
        let primarySetSelectAllEntityCheckboxValue
        let secondaryEntitySelectAllCheckboxValue
        let secondaryEntityCheckboxValues


        if(entity === 'producer') {
            primaryEntityCheckboxValues = [...producerCheckboxValues]
            secondaryEntityCheckboxValues = marketCheckboxValues
            primarySetEntityCheckboxValues = setProducerCheckboxValues
            primarySetSelectAllEntityCheckboxValue = setSelectAllProducerCheckboxValue
            secondaryEntitySelectAllCheckboxValue = selectAllMarketsCheckboxValue
        } else {
            primaryEntityCheckboxValues = [...marketCheckboxValues]
            secondaryEntityCheckboxValues = producerCheckboxValues
            primarySetEntityCheckboxValues = setMarketCheckboxValues
            primarySetSelectAllEntityCheckboxValue = setSelectAllMarketsCheckboxValue
            secondaryEntitySelectAllCheckboxValue = selectAllProducerCheckboxValue
        }
        
        let entityDataValue
        if (checked) {
            entityDataValue = primaryEntityCheckboxValues[index].value.map(item => ({
                ...item, checked: true
            }))

        } else {
            entityDataValue = primaryEntityCheckboxValues[index].value.map(item => ({
                ...item, checked: false
            }))
        }

        primaryEntityCheckboxValues[index] = { ...primaryEntityCheckboxValues[index], value: entityDataValue, checked }
        primarySetEntityCheckboxValues(primaryEntityCheckboxValues)

        const primaryEntityMapMarkerData = primaryEntityCheckboxValues.reduce((acc, entityData) => {
            if (entityData.checked) {
                return [...acc, ...entityData.value]
            } else {
                const currentEntityData = entityData.value
                const entityDataIncluded = []
                currentEntityData.forEach(entityDataItem => {
                    if (entityDataItem.checked) {
                        entityDataIncluded.push(entityDataItem)
                    }
                })

                return [...acc, ...entityDataIncluded]
            }
        }, [])

        let secondaryEntityMapMarkerData = [];
        if (secondaryEntitySelectAllCheckboxValue) {
            secondaryEntityMapMarkerData = secondaryEntityCheckboxValues.reduce((acc, entityData) => {
                return [...acc, ...entityData.value]
            }, [])
        } else {
            secondaryEntityMapMarkerData = secondaryEntityCheckboxValues.reduce((acc, entityData) => {
                if (entityData.checked) {
                    return [...acc, ...entityData.value]
                } else {
                    const currentEntityData = entityData.value
                    const entityDataIncluded = []
                    currentEntityData.forEach(entityDataItem => {
                        if (entityDataItem.checked) {
                            entityDataIncluded.push(entityDataItem)
                        }
                    })

                    return [...acc, ...entityDataIncluded]
                }
            }, [])
        }

        setShowedMarkerData(secondaryEntityMapMarkerData.concat(primaryEntityMapMarkerData))

        const allPrimaryEntityChecked = primaryEntityCheckboxValues.every(item => item.checked === true)
        if (allPrimaryEntityChecked) {
            primarySetSelectAllEntityCheckboxValue(true)
        }

        const allEntityCoordinateData = primaryEntityCheckboxValues[index].value.map(({ lat, lng }) => (
            [lat, lng]
        ))

        const [lat, lng] = getLatLngCenter(allEntityCoordinateData)
        
        const mapLocation = {
            center: {
                lat,
                lng,
            },
            zoom: 7
        };

        setCameraProps(mapLocation)
    }

    const handleCheckAll = (e, entity) => {
        setSelectSearchValue(null)
        setSelectedCoordenate(null)
        setShowedMarkerData([])
        setCloseInfo(true)

        let primaryEntityCheckboxValues
        let primarySetEntityCheckboxValues
        let primarySetSelectAllEntityCheckboxValue
        let secondaryEntitySelectAllCheckboxValue
        let secondaryEntityCheckboxValues


        if(entity === 'producer') {
            primaryEntityCheckboxValues = [...producerCheckboxValues]
            secondaryEntityCheckboxValues = marketCheckboxValues
            primarySetEntityCheckboxValues = setProducerCheckboxValues
            primarySetSelectAllEntityCheckboxValue = setSelectAllProducerCheckboxValue
            secondaryEntitySelectAllCheckboxValue = selectAllMarketsCheckboxValue
        } else {
            primaryEntityCheckboxValues = [...marketCheckboxValues]
            secondaryEntityCheckboxValues = producerCheckboxValues
            primarySetEntityCheckboxValues = setMarketCheckboxValues
            primarySetSelectAllEntityCheckboxValue = setSelectAllMarketsCheckboxValue
            secondaryEntitySelectAllCheckboxValue = selectAllProducerCheckboxValue
        }

        let newMapMarkerData = []
        let newEntityCheckboxValues;

        if (e.target.checked) {
            primarySetSelectAllEntityCheckboxValue(true)

            newMapMarkerData = primaryEntityCheckboxValues.reduce((acc, entityData) => {
                return [...acc, ...entityData.value]
            }, [])

            newEntityCheckboxValues = primaryEntityCheckboxValues.reduce((acc, entityData) => {
                const entityDataValue = entityData.value.map(item => ({
                    ...item, checked: true
                }))

                return [...acc, { ...entityData, value: entityDataValue, checked: true }]
            }, [])

        } else {
            primarySetSelectAllEntityCheckboxValue(false)

            newEntityCheckboxValues = primaryEntityCheckboxValues.reduce((acc, entityData) => {
                const entityDataValue = entityData.value.map(item => ({
                    ...item, checked: false
                }))

                return [...acc, { ...entityData, value: entityDataValue, checked: false }]
            }, [])
        }

        primarySetEntityCheckboxValues(newEntityCheckboxValues)

        let secondaryEntityMapMarkerData = [];
        if (secondaryEntitySelectAllCheckboxValue) {
            secondaryEntityMapMarkerData = secondaryEntityCheckboxValues.reduce((acc, entityData) => {
                return [...acc, ...entityData.value]
            }, [])
        } else {
            secondaryEntityMapMarkerData = secondaryEntityCheckboxValues.reduce((acc, entityData) => {
                if (entityData.checked) {
                    return [...acc, ...entityData.value]
                } else {
                    const currentEntityData = entityData.value
                    const entityDataIncluded = []
                    currentEntityData.forEach(leaseData => {
                        if (leaseData.checked) {
                            entityDataIncluded.push(leaseData)
                        }
                    })

                    return [...acc, ...entityDataIncluded]
                }
            }, [])
        }

        setShowedMarkerData(newMapMarkerData.concat(secondaryEntityMapMarkerData))
        setCameraProps(INITIAL_CAMERA)
    }

    const handleCheckSingleEntity = (checked, entityIndex, singleEntityIndex, entity) => {
        resetMapInputData()
        let primaryEntityCheckboxValues
        let primarySetEntityCheckboxValues
        let primarySetSelectAllEntityCheckboxValue
        let secondaryEntitySelectAllCheckboxValue
        let secondaryEntityCheckboxValues
        let setSingleEntityColor
        let setRandomCoordenate

        if(entity === 'producer') {
            primaryEntityCheckboxValues = [...producerCheckboxValues]
            secondaryEntityCheckboxValues = marketCheckboxValues
            primarySetEntityCheckboxValues = setProducerCheckboxValues
            primarySetSelectAllEntityCheckboxValue = setSelectAllProducerCheckboxValue
            secondaryEntitySelectAllCheckboxValue = selectAllMarketsCheckboxValue
            setSingleEntityColor = (color) => setLeaseSelectedColor(color)
            setRandomCoordenate = setRandomCoordenateValue
        } else {
            primaryEntityCheckboxValues = [...marketCheckboxValues]
            secondaryEntityCheckboxValues = producerCheckboxValues
            primarySetEntityCheckboxValues = setMarketCheckboxValues
            primarySetSelectAllEntityCheckboxValue = setSelectAllMarketsCheckboxValue
            secondaryEntitySelectAllCheckboxValue = selectAllProducerCheckboxValue
            setSingleEntityColor = () => setStationSelectedColor("red")
            setRandomCoordenate = setSecondRandomCoordenateValue
        }

        primaryEntityCheckboxValues[entityIndex].value[singleEntityIndex].checked = checked

        if (checked) {
            const allSingleEntityChecked = primaryEntityCheckboxValues[entityIndex].value.every(singleEntity => (
                singleEntity.checked === true
            ))
            if (allSingleEntityChecked) {
                primaryEntityCheckboxValues[entityIndex].checked = true
            } else {
                primaryEntityCheckboxValues[entityIndex].checked = false
            }
        } else {
            primarySetSelectAllEntityCheckboxValue(false)
            primaryEntityCheckboxValues[entityIndex].checked = false
        }

        primarySetEntityCheckboxValues(primaryEntityCheckboxValues)

        const currentPrimaryMapMarkerData = primaryEntityCheckboxValues.reduce((acc, entityData) => {
            if (entityData.checked) {
                return [...acc, ...entityData.value]
            } else {
                const currentEntityData = entityData.value
                const entityDataDataIncluded = []
                currentEntityData.forEach(singleEntityData => {
                    if (singleEntityData.checked) {
                        entityDataDataIncluded.push(singleEntityData)
                    }
                })

                return [...acc, ...entityDataDataIncluded]
            }
        }, [])

        let currentSecondaryMapMarkerData = [];
        if (secondaryEntitySelectAllCheckboxValue) {
            currentSecondaryMapMarkerData = secondaryEntityCheckboxValues.reduce((acc, entityData) => {
                return [...acc, ...entityData.value]
            }, [])
        } else {
            currentSecondaryMapMarkerData = secondaryEntityCheckboxValues.reduce((acc, entityData) => {
                if (entityData.checked) {
                    return [...acc, ...entityData.value]
                } else {
                    const currentSingleEntityData = entityData.value
                    const singleEntityDataIncluded = []
                    currentSingleEntityData.forEach(singleEntityItem => {
                        if (singleEntityItem.checked) {
                            singleEntityDataIncluded.push(singleEntityItem)
                        }
                    })

                    return [...acc, ...singleEntityDataIncluded]
                }
            }, [])
        }

        setShowedMarkerData(currentPrimaryMapMarkerData.concat(currentSecondaryMapMarkerData))

        setOpenWindowSelectedCoordenate(primaryEntityCheckboxValues[entityIndex].value[singleEntityIndex])

        const allEntityChecked = primaryEntityCheckboxValues.every(item => item.checked === true)
        if (allEntityChecked) {
            primarySetSelectAllEntityCheckboxValue(true)
        }
        
        if (checked) {
            const latitude = primaryEntityCheckboxValues[entityIndex].value[singleEntityIndex].lat
            const longitude = primaryEntityCheckboxValues[entityIndex].value[singleEntityIndex].lng

            const mapLocation = {
                center: {
                    lat: latitude,
                    lng: longitude,
                },
                zoom: 14
            };

            setSingleEntityColor(primaryEntityCheckboxValues[entityIndex].value[singleEntityIndex].color)
            const coordinate = `${latitude}, ${longitude}`


            setRandomCoordenate(coordinate)
            setCameraProps(mapLocation)
        }
    }
    
    const handleClusterView = () => {
        setIsClustered(!isClustered)

        setShowedMarkerData([])
        setCloseInfo(false)

        let currentProducerMapMarkerData = [];
        if (selectAllProducerCheckboxValue) {
            currentProducerMapMarkerData = producerCheckboxValues.reduce((acc, producerData) => {
                return [...acc, ...producerData.value]
            }, [])
        } else {
            currentProducerMapMarkerData = producerCheckboxValues.reduce((acc, producerData) => {
                if (producerData.checked) {
                    return [...acc, ...producerData.value]
                } else {
                    const currentProducerLeaseData = producerData.value
                    const producerLeaseDataIncluded = []
                    currentProducerLeaseData.forEach(leaseData => {
                        if (leaseData.checked) {
                            producerLeaseDataIncluded.push(leaseData)
                        }
                    })

                    return [...acc, ...producerLeaseDataIncluded]
                }
            }, [])
        }

        let currentMarketMapMarkerData = [];
        if (selectAllMarketsCheckboxValue) {
            currentMarketMapMarkerData = marketCheckboxValues.reduce((acc, marketData) => {
                return [...acc, ...marketData.value]
            }, [])
        } else {
            currentMarketMapMarkerData = marketCheckboxValues.reduce((acc, marketData) => {
                if (marketData.checked) {
                    return [...acc, ...marketData.value]
                }

                return acc
            }, [])
        }

        setShowedMarkerData(currentProducerMapMarkerData.concat(currentMarketMapMarkerData))
    }

    const isWithinUS = (latitude, longitude) => {
        const US_BOUNDS = {
            north: 49.3457868,
            south: 24.396308,
            west: -124.7844079,
            east: -66.9513812
        };

        return (
            latitude >= US_BOUNDS.south &&
            latitude <= US_BOUNDS.north &&
            longitude >= US_BOUNDS.west &&
            longitude <= US_BOUNDS.east
        );
    };

    const addCoordinate = (randomCoordenate, applyZoom = true) => {
        const coordenateValue = randomCoordenate === "first" ? randomCoordenateValue : secondRandomCoordenateValue
        const setCoordenateValueError = randomCoordenate === "first" ? setRandomCoordinateValueError : setSecondRandomCoordinateValueError
        setCoordenateValueError(false)

        let [latitude, longitude] = coordenateValue.split(',')

        if (!latitude || !longitude) {
            return
        }

        latitude = Number(latitude.trim())
        longitude = Number(longitude.trim())

        if (!isWithinUS(latitude, longitude)) {
            setCoordenateValueError(true)
            return
        }

        const coordinateName = randomCoordenate === "first" ? "Coordinate 1" : "Coordinate 2"
        const mapPointData = {
            "lease": coordinateName,
            "lat": latitude,
            "lng": longitude,
            "producer": null,
            "api": null,
            "bsw": null,
            "bpd": null,
            "color": "#00000",
            "key": coordinateName + "-key",
        }

        if (randomCoordenate === "first") {
            setRandomCoordenateMapMarkerData([mapPointData])
            setLeaseSelectedColor("black")
        } else {
            setStationSelectedColor("black")
            setSecondRandomCoordenateMapMarkerData([mapPointData])
        }

        if(applyZoom) {
            const mapLocation = {
                center: {
                    lat: latitude,
                    lng: longitude,
                },
                zoom: 14
            };
    
            setCameraProps(mapLocation)
        }

    }

    const addCoordinateOnChange = (newValue, setCoordinateValue, setCoordinateValueError) => {
        setCoordinateValueError(false)
        const validValue = newValue.replace(/[^0-9, .-]/g, '');
        setCoordinateValue(validValue)
    }

    const handleToggleInput = () => {
        setToggleInputView(!toggleInputView)
    }

    if (currentUser && !isLoading) {
        return (
            <ErrorBoundary>
                <div className='map-mainContainer' style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Top tab toggle */}
                    <div style={{ padding: '6px 12px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                        <TabToggle
                            tabOptions={[
                                { label: 'Map', value: 'map' },
                                ...(canAccessRFP ? [{ label: 'RFP Tool', value: 'rfp' }] : []),
                            ]}
                            activeTab={mapTab}
                            onChange={setMapTab}
                        />
                    </div>

                    {mapTab === 'rfp' && canAccessRFP ? (
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            <RFPTab />
                        </div>
                    ) : (
                    <div className='map-sidePanel'>
                        <div style={{ 'width': '25%', border: "1px solid darkgray", padding: "5px", display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <div style={{maxHeight: '55%'}}>
                                <div>
                                    <LeaseDropdown leaseCoordenates={leaseCoordenates} handleSelectCoordenate={handleSelectCoordenate} selectSearchValue={selectSearchValue} />
                                    <StationDropdown stationCoordenates={stationCoordenates} handleSelectStationCoordenate={handleSelectStationCoordenate} selectStationSearchValue={selectStationSearchValue} />
                                </div>

                                <AddCoordinateSection
                                    setShowAddCoordinateDropdown={setShowAddCoordinateDropdown}
                                    showAddCoordinateDropdown={showAddCoordinateDropdown}
                                    leaseSelectedColor={leaseSelectedColor}
                                    randomCoordenateValue={randomCoordenateValue}
                                    addCoordinateOnChange={addCoordinateOnChange}
                                    setRandomCoordenateValue={setRandomCoordenateValue}
                                    setRandomCoordinateValueError={setRandomCoordinateValueError}
                                    addCoordinate={addCoordinate}
                                    randomCoordinateValueError={randomCoordinateValueError}
                                    stationSelectedColor={stationSelectedColor}
                                    secondRandomCoordenateValue={secondRandomCoordenateValue}
                                    setSecondRandomCoordenateValue={setSecondRandomCoordenateValue}
                                    setSecondRandomCoordinateValueError={setSecondRandomCoordinateValueError}
                                    secondRandomCoordinateValueError={secondRandomCoordinateValueError} />

                                <MapControlButtons
                                    isClustered={isClustered}
                                    handleClusterView={handleClusterView}
                                    clearMap={clearMap}
                                    randomCoordenateValue={randomCoordenateValue}
                                    secondRandomCoordenateValue={secondRandomCoordenateValue}
                                    calculateBestRoute={calculateBestRoute}
                                    analyzeBestRoutes={analyzeBestRoutes}
                                    resetCalculation={resetCalculation} 
                                    toggleInput={handleToggleInput}
                                />
                            </div>

                            {toggleInputView ? (
                                <DataInput 
                                    onAddToMap={handleAddToMap}
                                />
                            ) : (
                                <div className={`producerMarketSection ${showAddCoordinateDropdown ? 'producerMarketSection-open' : 'producerMarketSection-close'}`}>
                                    <ProducersSection
                                        handleCheckAll={handleCheckAll}
                                        selectAllProducerCheckboxValue={selectAllProducerCheckboxValue}
                                        producerCheckboxValues={producerCheckboxValues}
                                        toggleDropdown={toggleDropdown}
                                        handleCheck={handleCheck}
                                        handleCheckProducerLease={handleCheckSingleEntity}
                                        showAddCoordinateDropdown={showAddCoordinateDropdown} />

                                    <MarketsSection
                                        handleCheckAllMarket={handleCheckAll}
                                        selectAllMarketsCheckboxValue={selectAllMarketsCheckboxValue}
                                        marketCheckboxValues={marketCheckboxValues}
                                        toggleMarketDropdown={toggleMarketDropdown}
                                        handleCheckMarket={handleCheck}
                                        handleCheckMarketStation={handleCheckSingleEntity}
                                        showAddCoordinateDropdown={showAddCoordinateDropdown} />
                                </div>
                            )}
                        </div>
                        <MapComponent
                            cameraProps={cameraProps}
                            handleCameraChange={handleCameraChange}
                            mapMarkerData={mapMarkerData}
                            isClustered={isClustered}
                            closeInfo={closeInfo}
                            setCloseInfo={setCloseInfo}
                            openWindowSelectedCoordenate={openWindowSelectedCoordenate}
                            openWindowSelectedMarketCoordenate={openWindowSelectedMarketCoordenate}
                            setOpenWindowSelectedMarketCoordenate={setOpenWindowSelectedMarketCoordenate}
                            setOpenWindowSelectedCoordenate={setOpenWindowSelectedCoordenate}
                            randomCoordenateMapMarkerData={randomCoordenateMapMarkerData}
                            secondRandomCoordenateMapMarkerData={secondRandomCoordenateMapMarkerData}
                            routeCalculation={routeCalculation}
                            routeCalculations={routeCalculations}
                            truckingRates={truckingRates}
                            isLoadingZoomCamera={isLoadingZoomCamera}
                        />
                    </div>
                    )}
                </div>
            </ErrorBoundary>
        )
    } else {
        return (
            <div className='fullscreen'>
                <div className="spinner"></div>
            </div>
        )
    }
}

export default Home